// ---------------------------------------------------------------------------
// useFocusTrap
//
// While `active` is true:
//   - Moves focus into the returned container ref on mount (tabIndex={-1} required
//     on the element).
//   - Traps Tab / Shift+Tab within all focusable descendants.
//   - Restores focus to whichever element was active when `active` became true.
//
// Usage:
//   const trapRef = useFocusTrap(open)
//   <div ref={trapRef} tabIndex={-1} role="dialog" aria-modal="true">…</div>
// ---------------------------------------------------------------------------

import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(active: boolean): React.RefObject<HTMLDivElement | null> {
  const containerRef = useRef<HTMLDivElement>(null);
  // Capture the element that had focus before the dialog opened so we can
  // restore it when the dialog closes.
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!active) return;

    // Save the currently focused element so we can restore it on close.
    previousFocusRef.current = document.activeElement;

    // Move focus into the container on the next tick so the element is
    // guaranteed to be in the DOM.
    const frame = requestAnimationFrame(() => {
      containerRef.current?.focus();
    });

    // Tab / Shift+Tab trap
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.closest("[hidden]") && el.offsetParent !== null,
      );

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === container) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || document.activeElement === container) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to the element that was active before the dialog opened.
      if (previousFocusRef.current && typeof (previousFocusRef.current as HTMLElement).focus === "function") {
        (previousFocusRef.current as HTMLElement).focus();
      }
    };
  }, [active]);

  return containerRef;
}
